import * as React from 'react';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Box, Paper, Snackbar, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';
import useCalendarState from './hooks/useCalendarState';
import useClampedMenuPosition from './hooks/useClampedMenuPosition';
import useCalendarColors from './hooks/useCalendarColors';
import { belongsInSidebar } from './calendarConstants';
import StatusBadge from '../shared/StatusBadge';
import ConfirmRemoveScheduleDialog from '../maintenances/ConfirmRemoveScheduleDialog';
import { useMaintenanceStateChange } from '../maintenances/useMaintenanceStateChange';

const SUBMENU_CLOSE_DELAY_MS = 180; // lets the pointer cross from the row into the submenu

// Forward path first, then the side states, then cancel. `quote` is deliberately absent:
// the backend deletes every schedule on planned -> quote, which is not a calendar action.
const STATE_ORDER = [
  'planned',
  'in_progress',
  'completed',
  'picked_up',
  'waiting',
  'vehicle_in_workshop',
  'cancelled',
];

/**
 * Rows for the state submenu. The current state is shown but disabled. From in_progress
 * "completed" becomes two rows: with the customer notification dialog, and without.
 */
export function buildStateRows(currentState, getStateConfig) {
  const rows = [];
  for (const key of STATE_ORDER) {
    if (key === 'completed' && currentState === 'in_progress') {
      rows.push({
        key,
        mode: 'notify',
        labelKey: 'calendar.contextMenu.completeNotify',
        descriptionKey: 'calendar.contextMenu.completeNotifyDesc',
        testId: 'event-context-menu-state-completed',
      });
      rows.push({
        key,
        mode: 'silent',
        labelKey: 'calendar.contextMenu.completeSilent',
        descriptionKey: 'calendar.contextMenu.completeSilentDesc',
        testId: 'event-context-menu-state-completed-silent',
      });
      continue;
    }
    rows.push({
      key,
      mode: undefined,
      labelKey: `status.${key}`,
      testId: `event-context-menu-state-${key}`,
      disabled: key === currentState,
      dotColor: getStateConfig ? getStateConfig(key).color : undefined,
    });
  }
  return rows.map((row) => ({
    ...row,
    dotColor: row.dotColor ?? (getStateConfig ? getStateConfig(row.key).color : undefined),
    dotShape: 'circle',
  }));
}

/**
 * Rows for the colour tag submenu: one per active tag, the current one disabled. Inactive
 * tags are never offered, but a card already carrying one keeps its colour.
 */
export function buildColorTagRows(activeTags, currentTagId) {
  return activeTags.map((tag) => ({
    key: `tag-${tag.id}`,
    tagId: tag.id,
    label: tag.name,
    testId: `event-context-menu-color-tag-${tag.id}`,
    disabled: tag.id === currentTagId,
    dotColor: tag.color,
    // A ring tag is drawn as an outline here too, so the two modes read differently.
    dotShape: tag.display_mode === 'border' ? 'ring' : 'square',
  }));
}

/**
 * Right-click menu on a maintenance card (desktop calendar): open the work order, change
 * its state (hover submenu), remove this scheduling. Always mounted in MainCalendarView:
 * the popup is conditional, but the dialogs of the actions must survive the popup closing,
 * so they and the state-change hook live here rather than inside the popup.
 */
function EventContextMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const eventContextMenu = useCalendarState((s) => s.eventContextMenu);
  const closeEventContextMenu = useCalendarState((s) => s.closeEventContextMenu);
  const openEventContextMenu = useCalendarState((s) => s.openEventContextMenu);
  const updateEventsByMaintenance = useCalendarState((s) => s.updateEventsByMaintenance);
  const removeEventsByMaintenance = useCalendarState((s) => s.removeEventsByMaintenance);
  const removeEvent = useCalendarState((s) => s.removeEvent);
  const addToUnscheduled = useCalendarState((s) => s.addToUnscheduled);
  const updateUnscheduledMaintenance = useCalendarState((s) => s.updateUnscheduledMaintenance);
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const { activeTags, getStateConfig } = useCalendarColors();
  const canChangeState = can(currentOrganization, 'work_orders.change_state');
  const canSchedule = can(currentOrganization, 'work_orders.schedule');
  const canTag = can(currentOrganization, 'work_orders.edit');
  // The tag list is managed in the admin-only Ominaisuudet settings tab.
  const canManageTags = can(currentOrganization, 'settings.features');

  // { event, hasMultiple } while the remove-schedule confirmation is open
  const [unscheduleTarget, setUnscheduleTarget] = useState(null);
  const [unscheduleBusy, setUnscheduleBusy] = useState(false);
  const [snack, setSnack] = useState(null);

  // The calendar feed excludes cancelled work orders, so their cards go away locally too.
  // Everything else is a repaint of every card sharing the maintenance_id. No refetch:
  // set_state never touches schedule rows for the transitions offered here.
  const handleChanged = useCallback((detail) => {
    if (!detail?.id) return;
    if (detail.state === 'cancelled') {
      removeEventsByMaintenance(detail.id);
    } else {
      updateEventsByMaintenance(detail.id, {
        state: detail.state,
        has_active_booking: detail.has_active_booking,
      });
    }
  }, [removeEventsByMaintenance, updateEventsByMaintenance]);

  const { changeState, busy, dialogs } = useMaintenanceStateChange({ onChanged: handleChanged });

  // A menu left open when the calendar unmounts must not reappear with a stale event.
  useEffect(() => () => closeEventContextMenu(), [closeEventContextMenu]);

  const otherCardsOf = (ev) =>
    useCalendarState
      .getState()
      .events.filter((e) => e.maintenance_id === ev.maintenance_id && e.id !== ev.id);

  const handleOpenWorkOrder = (ev) => {
    closeEventContextMenu();
    navigate(`/maintenances/${ev.maintenance_id}`);
  };

  const handlePickState = (targetState, mode) => {
    const ev = eventContextMenu?.event;
    if (!ev) return;
    closeEventContextMenu(); // menu and backdrop gone before any dialog opens
    changeState({ maintenanceId: ev.maintenance_id, targetState, mode });
  };

  // A tag belongs to the work order, so every card of it repaints, sidebar row included.
  // No refetch: set-color-tag touches nothing else.
  const handlePickColorTag = async (tagId) => {
    const ev = eventContextMenu?.event;
    if (!ev) return;
    closeEventContextMenu();
    try {
      await axios.post(`/api/maintenances/${ev.maintenance_id}/set-color-tag/`, { color_tag: tagId });
      updateEventsByMaintenance(ev.maintenance_id, { color_tag_id: tagId });
      updateUnscheduledMaintenance(ev.maintenance_id, { color_tag_id: tagId });
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      // A 403 with a string detail is already toasted by the global PermissionDeniedSnackbar
      if (!(status === 403 && typeof data?.detail === 'string')) {
        setSnack({ severity: 'error', message: t('calendar.contextMenu.colorTagFailed') });
      }
    }
  };

  const handleOpenColorTagSettings = () => {
    closeEventContextMenu();
    navigate('/settings?tab=features&subtab=calendar');
  };

  const handleAskUnschedule = (ev) => {
    closeEventContextMenu();
    setUnscheduleTarget({ event: ev, hasMultiple: otherCardsOf(ev).length > 0 });
  };

  // Same flow as ScheduleDialog's delete: remove the schedule row only (employee
  // assignments stay), drop the card, and put the work order back in the unscheduled
  // list when this was its last card.
  const handleConfirmUnschedule = async () => {
    const ev = unscheduleTarget?.event;
    if (!ev) return;
    setUnscheduleBusy(true);
    try {
      await axios.delete(`/api/scheduling/schedules/${ev.id}/`);
      const remaining = otherCardsOf(ev);
      removeEvent(ev.id);
      if (remaining.length === 0 && belongsInSidebar(ev.state)) {
        addToUnscheduled({
          id: ev.maintenance_id,
          vehicle_registration: ev.vehicle_registration,
          customer_name: ev.customer_name,
          description: ev.description,
          priority: 'normal',
          created_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      // A 403 with a string detail is already toasted by the global PermissionDeniedSnackbar
      if (!(status === 403 && typeof data?.detail === 'string')) {
        setSnack({ severity: 'error', message: data?.message || t('calendar.contextMenu.unscheduleFailed') });
      }
    } finally {
      setUnscheduleBusy(false);
      setUnscheduleTarget(null);
    }
  };

  // Right-click on another card while the menu is open re-targets, like a native menu.
  const handleBackdropContextMenu = (e) => {
    e.preventDefault();
    const nodes = document.elementsFromPoint(e.clientX, e.clientY);
    const cardEl = nodes.find(
      (n) => n.dataset && n.dataset.event === 'true' && n.dataset.eventType === 'maintenance'
    );
    const target = cardEl
      ? useCalendarState.getState().events.find((ev) => String(ev.id) === cardEl.dataset.eventId)
      : null;
    if (target) {
      openEventContextMenu(target, { x: e.clientX, y: e.clientY });
    } else {
      closeEventContextMenu();
    }
  };

  return (
    <>
      {eventContextMenu && (
        <EventContextMenuPopup
          event={eventContextMenu.event}
          position={eventContextMenu.position}
          busy={busy}
          canChangeState={canChangeState}
          canSchedule={canSchedule}
          canTag={canTag}
          canManageTags={canManageTags}
          activeTags={activeTags}
          getStateConfig={getStateConfig}
          onClose={closeEventContextMenu}
          onBackdropContextMenu={handleBackdropContextMenu}
          onOpenWorkOrder={handleOpenWorkOrder}
          onPickState={handlePickState}
          onPickColorTag={handlePickColorTag}
          onOpenColorTagSettings={handleOpenColorTagSettings}
          onUnschedule={handleAskUnschedule}
          t={t}
        />
      )}
      {dialogs}
      <ConfirmRemoveScheduleDialog
        open={!!unscheduleTarget}
        handleClose={() => setUnscheduleTarget(null)}
        onConfirm={handleConfirmUnschedule}
        loading={unscheduleBusy}
        hasActiveBooking={!!unscheduleTarget?.event?.has_active_booking}
        schedule={unscheduleTarget?.hasMultiple ? unscheduleTarget.event : null}
        hasMultiple={!!unscheduleTarget?.hasMultiple}
      />
      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="event-context-menu-snackbar"
      >
        <Alert
          severity={snack?.severity || 'info'}
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ width: '100%' }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const rowFont = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

/** One top-level action row: tinted icon square, label, optional trailing chevron. */
const MenuRow = forwardRef(function MenuRow(
  { testId, icon, color, label, trailing, active, disabled, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  return (
    <Box
      ref={ref}
      data-testid={testId}
      role="menuitem"
      aria-disabled={disabled || undefined}
      aria-expanded={trailing ? !!active : undefined}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        bgcolor: active ? 'background.default' : 'transparent',
        '&:hover': { bgcolor: disabled ? 'transparent' : 'background.default' },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: `${color}15`,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ ...rowFont, fontSize: '14px', fontWeight: 600, flex: 1 }}>{label}</Typography>
      {trailing}
    </Box>
  );
});

function EventContextMenuPopup({
  event,
  position,
  busy,
  canChangeState,
  canSchedule,
  canTag,
  canManageTags,
  activeTags,
  getStateConfig,
  onClose,
  onBackdropContextMenu,
  onOpenWorkOrder,
  onPickState,
  onPickColorTag,
  onOpenColorTagSettings,
  onUnschedule,
  t,
}) {
  const { menuRef, adjustedPosition } = useClampedMenuPosition(true, position);
  const changeStateRowRef = useRef(null);
  const colorTagRowRef = useRef(null);
  const closeTimerRef = useRef(null);
  // { kind: 'state' | 'tag', menuRect, rowRect } while a submenu is open
  const [submenuAnchor, setSubmenuAnchor] = useState(null);

  const cancelSubmenuClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openSubmenu = useCallback(
    (kind) => {
      cancelSubmenuClose();
      const rowRef = kind === 'tag' ? colorTagRowRef : changeStateRowRef;
      if (!menuRef.current || !rowRef.current) return;
      setSubmenuAnchor({
        kind,
        menuRect: menuRef.current.getBoundingClientRect(),
        rowRect: rowRef.current.getBoundingClientRect(),
      });
    },
    [cancelSubmenuClose, menuRef]
  );

  const closeSubmenuSoon = useCallback(() => {
    cancelSubmenuClose();
    closeTimerRef.current = setTimeout(() => setSubmenuAnchor(null), SUBMENU_CLOSE_DELAY_MS);
  }, [cancelSubmenuClose]);

  useEffect(() => cancelSubmenuClose, [cancelSubmenuClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop: click closes, right-click re-targets */}
      <Box
        onClick={onClose}
        onContextMenu={onBackdropContextMenu}
        sx={{ position: 'fixed', inset: 0, zIndex: 999 }}
      />

      <Paper
        ref={menuRef}
        data-testid="calendar-event-context-menu"
        role="menu"
        elevation={8}
        sx={{
          position: 'fixed',
          top: adjustedPosition.y,
          left: adjustedPosition.x,
          zIndex: 1000,
          minWidth: 280,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          data-testid="event-context-menu-header"
          sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography sx={{ ...rowFont, fontSize: '16px', fontWeight: 700 }}>
            {event.vehicle_registration}
            {event.work_number ? (
              <Typography
                component="span"
                sx={{ ml: 1, fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}
              >
                #{event.work_number}
              </Typography>
            ) : null}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <StatusBadge status={event.state} size="small" data-testid="event-context-menu-current-state" />
          </Box>
        </Box>

        <Box sx={{ p: 1 }}>
          <MenuRow
            testId="event-context-menu-open-work-order"
            icon={<OpenInNewIcon fontSize="small" />}
            color="#3b82f6"
            label={t('Open maintenance')}
            onClick={() => onOpenWorkOrder(event)}
            onMouseEnter={closeSubmenuSoon}
          />
          {canChangeState && (
            <MenuRow
              ref={changeStateRowRef}
              testId="event-context-menu-change-state"
              icon={<SwapHorizIcon fontSize="small" />}
              color="#8b5cf6"
              label={t('calendar.contextMenu.changeState')}
              trailing={<ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
              active={submenuAnchor?.kind === 'state'}
              disabled={busy}
              onClick={() =>
                submenuAnchor?.kind === 'state' ? setSubmenuAnchor(null) : openSubmenu('state')
              }
              onMouseEnter={busy ? undefined : () => openSubmenu('state')}
              onMouseLeave={closeSubmenuSoon}
            />
          )}
          {canTag && (
            <MenuRow
              ref={colorTagRowRef}
              testId="event-context-menu-color-tag"
              icon={<LabelOutlinedIcon fontSize="small" />}
              color="#0d9488"
              label={t('calendar.contextMenu.addColorTag')}
              trailing={<ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
              active={submenuAnchor?.kind === 'tag'}
              onClick={() => (submenuAnchor?.kind === 'tag' ? setSubmenuAnchor(null) : openSubmenu('tag'))}
              onMouseEnter={() => openSubmenu('tag')}
              onMouseLeave={closeSubmenuSoon}
            />
          )}
          {canTag && event.color_tag_id && (
            <MenuRow
              testId="event-context-menu-color-tag-remove"
              icon={<LabelOffOutlinedIcon fontSize="small" />}
              color="#64748b"
              label={t('calendar.contextMenu.removeColorTag')}
              onClick={() => onPickColorTag(null)}
              onMouseEnter={closeSubmenuSoon}
            />
          )}
          {canSchedule && (
            <MenuRow
              testId="event-context-menu-unschedule"
              icon={<EventBusyIcon fontSize="small" />}
              color="#ef4444"
              label={t('Remove schedule')}
              onClick={() => onUnschedule(event)}
              onMouseEnter={closeSubmenuSoon}
            />
          )}
        </Box>
      </Paper>

      {submenuAnchor?.kind === 'state' && (
        <HoverSubmenu
          anchor={submenuAnchor}
          testId="event-context-menu-state-submenu"
          rows={buildStateRows(event.state, getStateConfig)}
          busy={busy}
          onPick={(row) => onPickState(row.key, row.mode)}
          onMouseEnter={cancelSubmenuClose}
          onMouseLeave={closeSubmenuSoon}
          t={t}
        />
      )}

      {submenuAnchor?.kind === 'tag' && (
        <HoverSubmenu
          anchor={submenuAnchor}
          testId="event-context-menu-color-tag-submenu"
          rows={buildColorTagRows(activeTags, event.color_tag_id)}
          busy={false}
          emptyContent={
            <>
              <Box
                data-testid="event-context-menu-color-tag-empty"
                sx={{ px: 1.5, py: 1, fontSize: '13px', color: 'text.secondary', maxWidth: 260 }}
              >
                {t('calendar.contextMenu.noColorTags')}
              </Box>
              {canManageTags && (
                <MenuRow
                  testId="event-context-menu-color-tag-settings"
                  icon={<SettingsOutlinedIcon fontSize="small" />}
                  color="#0d9488"
                  label={t('calendar.contextMenu.openSettings')}
                  onClick={onOpenColorTagSettings}
                />
              )}
            </>
          }
          onPick={(row) => onPickColorTag(row.tagId)}
          onMouseEnter={cancelSubmenuClose}
          onMouseLeave={closeSubmenuSoon}
          t={t}
        />
      )}
    </>
  );
}

/**
 * A submenu opened to the right of its row (to the left when out of room). Shared by the
 * state list and the colour tag list so both behave and position identically.
 */
function HoverSubmenu({ anchor, testId, rows, busy, emptyContent, onPick, onMouseEnter, onMouseLeave, t }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: anchor.menuRect.right, y: anchor.rowRect.top });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const margin = 8;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let x = anchor.menuRect.right - 4; // slight overlap so the pointer never crosses the backdrop
    let y = anchor.rowRect.top - 8;
    if (x + w > window.innerWidth - margin) x = anchor.menuRect.left - w + 4;
    if (y + h > window.innerHeight - margin) y = window.innerHeight - h - margin;
    if (x < margin) x = margin;
    if (y < margin) y = margin;
    setPos({ x, y });
  }, [anchor]);

  return (
    <Paper
      ref={ref}
      data-testid={testId}
      role="menu"
      elevation={8}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'fixed',
        top: pos.y,
        left: pos.x,
        zIndex: 1001,
        minWidth: 220,
        borderRadius: '12px',
        p: 1,
      }}
    >
      {rows.length === 0 && emptyContent}
      {rows.map((row) => {
        const disabled = !!row.disabled || busy;
        return (
          <Box
            key={row.testId}
            data-testid={row.testId}
            role="menuitem"
            aria-disabled={disabled || undefined}
            onClick={disabled ? undefined : () => onPick(row)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 1,
              borderRadius: '8px',
              cursor: disabled ? 'default' : 'pointer',
              opacity: row.disabled ? 0.45 : 1,
              '&:hover': { bgcolor: disabled ? 'transparent' : 'background.default' },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: row.dotShape === 'circle' ? '50%' : '2px',
                bgcolor: row.dotShape === 'ring' ? 'transparent' : row.dotColor || 'text.disabled',
                border: row.dotShape === 'ring' ? `2px solid ${row.dotColor}` : 'none',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ ...rowFont, fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {row.label || t(row.labelKey)}
              </Typography>
              {row.descriptionKey && (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{t(row.descriptionKey)}</Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}

export default EventContextMenu;
